/**
 * Panel Interviews tab (real Microsoft Graph data).
 *
 * Lists a candidate's panel interviews (`/api/v1/panels?candidate_id=&kind=`),
 * lets HR schedule a new one, sync artifacts from Graph, view transcript, and
 * play the recording. Tech panels also support AI evaluation + human ratings;
 * HR panels (Offer tab) show recording + transcript only.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Upload } from 'lucide-react';

import { v1Get } from '@/lib/api';
import { useHasPermission } from '@/auth/permissions';
import { KIND_COPY } from '@/components/panelInterviews/panelInterviewConstants';
import { SchedulePanelDialog } from '@/components/panelInterviews/SchedulePanelDialog';
import { ManualUploadDialog } from '@/components/panelInterviews/ManualUploadDialog';
import { PanelInterviewCard } from '@/components/panelInterviews/PanelInterviewCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function PanelInterviewsTab({
  candidateId,
  candidateEmail,
  disabled = false,
  panelKind = 'tech',
  evaluationId,
  panelRounds = [],
}) {
  const kind = panelKind === 'hr' ? 'hr' : 'tech';
  const copy = KIND_COPY[kind];
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const evalQs = evaluationId ? `&evaluation_id=${encodeURIComponent(evaluationId)}` : '';
  const { data: panels, isLoading } = useQuery({
    queryKey: ['v1-panels', candidateId, kind, evaluationId],
    queryFn: () => v1Get(`/panels?candidate_id=${candidateId}&kind=${kind}${evalQs}`),
    enabled: !!candidateId && !!evaluationId,
  });

  const livePanels = useMemo(
    () => (panels || []).filter((p) => p.status !== 'cancelled'),
    [panels],
  );

  const occupiedStageIds = useMemo(() => {
    const ids = new Set();
    livePanels.forEach((p) => {
      if (p.pipeline_stage_id) ids.add(p.pipeline_stage_id);
    });
    return ids;
  }, [livePanels]);

  const scopedStageIds = useMemo(() => {
    const ids = (panelRounds || []).map((r) => r.id).filter(Boolean);
    return ids.length ? new Set(ids) : null;
  }, [panelRounds]);

  const scopedPanels = useMemo(() => {
    if (kind !== 'tech' || !scopedStageIds) return livePanels;
    return livePanels.filter((p) => p.pipeline_stage_id && scopedStageIds.has(p.pipeline_stage_id));
  }, [livePanels, kind, scopedStageIds]);

  const vacantRounds = useMemo(() => {
    if (kind !== 'tech') return [];
    return (panelRounds || []).filter((r) => r.id && !occupiedStageIds.has(r.id));
  }, [kind, panelRounds, occupiedStageIds]);

  const hrOccupied = kind === 'hr' && livePanels.length > 0;

  const roundLabelById = useMemo(() => {
    const map = {};
    (panelRounds || []).forEach((r) => { if (r.id) map[r.id] = r.label || r.id; });
    return map;
  }, [panelRounds]);

  const roundTitle = kind === 'tech' && panelRounds.length === 1
    ? (panelRounds[0].label || copy.title)
    : copy.title;

  if (!evaluationId) {
    return (
      <Card data-testid={copy.testIdRoot}>
        <CardContent className="p-6 text-xs text-muted-foreground">
          Select an evaluation to view and schedule panel interviews.
        </CardContent>
      </Card>
    );
  }

  const scheduleDisabled = disabled || !candidateEmail
    || (kind === 'tech' && vacantRounds.length === 0)
    || hrOccupied;

  return (
    <div className="space-y-4" data-testid={copy.testIdRoot}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{roundTitle}</p>
          <p className="text-xs text-muted-foreground">
            {copy.subtitle}
            {kind === 'tech' && panelRounds.length > 1 && (
              <> · {panelRounds.length} rounds · {vacantRounds.length} vacant</>
            )}
            {kind === 'tech' && panelRounds.length === 1 && (
              <> · {vacantRounds.length ? 'Vacant' : 'Occupied'}</>
            )}
            {kind === 'hr' && (
              <> · {hrOccupied ? 'Occupied — cancel to reschedule' : 'Vacant'}</>
            )}
            {disabled && ' (Disabled: candidate is not in progress.)'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {canEditEvaluation && (
            <Button
              variant="outline"
              onClick={() => setManualOpen(true)}
              disabled={scheduleDisabled}
              className="gap-1.5"
              data-testid={kind === 'hr' ? 'manual-upload-hr-panel-button' : 'manual-upload-panel-button'}
            >
              <Upload className="h-4 w-4" /> Manual Upload
            </Button>
          )}
          {canEditEvaluation && (
            <Button
              onClick={() => setScheduleOpen(true)}
              disabled={scheduleDisabled}
              className="gap-1.5"
              data-testid={copy.testIdSchedule}
            >
              <Plus className="h-4 w-4" /> {copy.scheduleButton}
            </Button>
          )}
        </div>
      </div>

      {kind === 'tech' && panelRounds.length > 1 && (
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="panel-rounds-summary">
          {panelRounds.map((r) => {
            const occupied = occupiedStageIds.has(r.id);
            return (
              <li key={r.id} className="rounded-lg border bg-secondary/30 px-3 py-2 text-xs" data-testid={`panel-round-${r.id}`}>
                <p className="font-medium">{r.label || 'Panel round'}</p>
                <p className="text-muted-foreground">{occupied ? 'Occupied — cancel to reschedule' : 'Vacant'}</p>
              </li>
            );
          })}
        </ul>
      )}

      {isLoading ? (
        <Card><CardContent className="p-6 text-xs text-muted-foreground">{copy.loading}</CardContent></Card>
      ) : scopedPanels.length === 0 ? (
        <Card><CardContent className="p-6 text-xs text-muted-foreground">{copy.empty}</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {scopedPanels.map((p) => (
            <PanelInterviewCard
              key={p.id}
              panel={p}
              candidateId={candidateId}
              panelKind={kind}
              copy={copy}
              roundLabel={p.pipeline_stage_id ? roundLabelById[p.pipeline_stage_id] : null}
              canEditEvaluation={canEditEvaluation}
            />
          ))}
        </div>
      )}

      {canEditEvaluation && (
        <SchedulePanelDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          candidateId={candidateId}
          candidateEmail={candidateEmail}
          panelKind={kind}
          copy={copy}
          evaluationId={evaluationId}
          vacantRounds={vacantRounds}
        />
      )}
      {canEditEvaluation && (
        <ManualUploadDialog
          open={manualOpen}
          onOpenChange={setManualOpen}
          candidateId={candidateId}
          candidateEmail={candidateEmail}
          evaluationId={evaluationId}
          vacantRounds={vacantRounds}
          panelKind={kind}
        />
      )}
    </div>
  );
}

export default PanelInterviewsTab;
